import {
  FC,
  PropsWithChildren,
  Suspense,
  useCallback,
  useContext,
} from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { GlobalStyles, ThemeProvider, Typography } from '@mui/material';
import {
  AssociateSetupContext,
  AuthenticationContext,
} from '@vs-online-only-app/state';
import { CenteredLoader, theme } from '@vs-online-only-app/ui';
import AppSidebar from './AppSidebar';
import { TopBar } from '../TopBar';
import SnackNotifier from '../SnackNotifier';
import { useUnreadContactMessagesNotifier } from '../../hooks/useUnreadContactMessagesNotifier';

export enum AppRoutes {
  Home = '/',
  Chats = '/chats',
  Contacts = '/contacts',
  Products = '/products',
  Tutorials = '/tutorials',
  Settings = '/settings',
  Conversation = '/conversation',
}

const isStandalone = window === window.top;

const RootWrapper = styled.div`
  &.grid {
    display: grid;
    grid-template-rows: auto 1fr;
  }
  height: 100vh;
  background: ${({ theme }) => theme.palette.background.default};
  position: relative;
`;

const PageWrapper = styled.div`
  display: grid;
  grid-template-columns: ${isStandalone ? 'auto' : ''} 1fr;
  overflow: auto;
`;

const AppLayout: FC<PropsWithChildren> = ({ children }) => {
  const { isAuthenticated, logoutStatus } = useContext(AuthenticationContext);
  const { hasCompletedProfileSetup, hasCompletedIntroModules } = useContext(
    AssociateSetupContext,
  );
  const location = useLocation();
  const showTopBar = location.pathname !== AppRoutes.Conversation;
  useUnreadContactMessagesNotifier();

  const renderPage = useCallback(() => {
    if (['loading', 'success'].includes(logoutStatus)) {
      return (
        <CenteredLoader>
          <Typography variant="body1">Logging out</Typography>
        </CenteredLoader>
      );
    }

    if (isAuthenticated && !showTopBar) {
      return children;
    }
    const displayCompleteLayout =
      hasCompletedProfileSetup && hasCompletedIntroModules;
    if (isAuthenticated && displayCompleteLayout) {
      return (
        <PageWrapper>
          <AppSidebar />
          <SnackNotifier />
          {children}
        </PageWrapper>
      );
    }

    return children;
  }, [
    logoutStatus,
    isAuthenticated,
    children,
    hasCompletedProfileSetup,
    hasCompletedIntroModules,
    showTopBar,
  ]);

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles styles={{ '*': { boxSizing: 'border-box' } }} />
      <RootWrapper className={showTopBar ? 'grid' : ''}>
        {showTopBar && <TopBar />}
        <Suspense
          fallback={
            <CenteredLoader>
              <Typography variant="body1">Loading Layout</Typography>
            </CenteredLoader>
          }
        >
          {renderPage()}
        </Suspense>
      </RootWrapper>
    </ThemeProvider>
  );
};

export default AppLayout;
